<template>
  <div class="nd-page appointment-page">
    <section class="appointment-summary">
      <div class="appointment-summary__card">
        <span>Data e horário</span>
        <strong>{{ appointmentDateLabel }}</strong>
        <small>agenda operacional</small>
      </div>
      <div class="appointment-summary__card">
        <span>Paciente</span>
        <strong>{{ selectedPatientName }}</strong>
        <small>vínculo clínico</small>
      </div>
      <div class="appointment-summary__card">
        <span>Procedimento</span>
        <strong>{{ selectedProcedureName }}</strong>
        <small>plano de atendimento</small>
      </div>
      <div class="appointment-summary__card">
        <span>Status</span>
        <strong>{{ selectedStateText }}</strong>
        <small>situação da consulta</small>
      </div>
    </section>

    <v-card class="nd-section-card appointment-form-card">
      <v-form ref="appointmentForm" v-model="formValid">
        <div class="appointment-card-header">
          <div>
            <h2>Dados da consulta</h2>
            <p>Informe data, paciente, procedimento e status operacional.</p>
          </div>
          <v-chip v-if="appointment.id" class="appointment-id-chip" small label>
            Consulta #{{ appointment.id }}
          </v-chip>
        </div>

        <div class="appointment-form-grid">
          <v-text-field
            id="scheduledDate"
            label="Data agendada"
            v-model="appointment.scheduledDate"
            :rules="[rules.required]"
            outlined
            dense hide-details="auto"
            type="date"
          ></v-text-field>
          <v-text-field
            id="scheduledTime"
            label="Horário"
            v-model="appointment.scheduledTime"
            :rules="[rules.required]"
            outlined
            dense hide-details="auto"
            type="time"
          ></v-text-field>
          <v-autocomplete
            id="patientSelect"
            label="Paciente"
            v-model="appointment.patientId"
            :items="patients"
            item-text="name"
            item-value="id"
            :search-input.sync="patientSearch"
            :rules="[rules.required]"
            outlined
            dense hide-details="auto"
            clearable
            no-data-text="Nenhum paciente encontrado"
          >
            <template v-slot:append-item>
              <div v-if="canCreatePatientFromSearch" class="appointment-create-patient">
                <v-divider class="mb-2"></v-divider>
                <button type="button" class="appointment-create-patient__button" @click.stop="openPatientCreateDialog">
                  <v-icon size="18" color="#8f52d1">mdi-account-plus-outline</v-icon>
                  <span>Criar paciente "{{ normalizedPatientSearch }}"</span>
                </button>
              </div>
            </template>
          </v-autocomplete>
          <v-select
            id="procedureSelect"
            label="Procedimento principal"
            v-model="appointment.procedureId"
            :items="procedures"
            item-text="name"
            item-value="id"
            :rules="[rules.required]"
            outlined
            dense hide-details="auto"
            no-data-text="Nenhum procedimento encontrado"
          ></v-select>
          <v-select
            id="insuranceSelect"
            label="Convênio"
            v-model="appointment.insuranceId"
            :items="insurances"
            item-text="name"
            item-value="id"
            outlined
            dense hide-details="auto"
            clearable
            no-data-text="Nenhum convênio encontrado"
          ></v-select>
          <v-select
            id="stateSelect"
            label="Estado"
            v-model="appointment.state"
            :items="states"
            item-text="text"
            item-value="value"
            :rules="[rules.required]"
            outlined
            dense hide-details="auto"
          ></v-select>
        </div>

        <div class="appointment-editor-block">
          <div class="appointment-editor-block__title">
            <span>Descrição clínica</span>
            <small>Evolução, observações e orientações da consulta.</small>
          </div>
          <tiptap-vuetify
            id="description"
            v-model="appointment.description"
            class="appointment-rich-editor"
            :extensions="editorExtensions"
            placeholder="Descreva a evolução clínica, observações, combinados e próximos passos..."
          />
        </div>

        <div class="nd-form-actions appointment-actions">
          <v-btn id="btnCancel" type="button" color="primary" outlined elevation="0" @click="$router.push('/consultas')">
            Voltar
          </v-btn>
          <v-btn id="btnSave" type="button" color="primary" elevation="0" @click="saveAppointment()" :loading="loadingSave">
            Salvar
          </v-btn>
        </div>
      </v-form>
    </v-card>

    <v-card class="nd-section-card appointment-manager-card">
      <div class="appointment-card-header">
        <div>
          <h2>Painel gerencial da consulta</h2>
          <p>{{ appointment.id ? 'Continue o atendimento com prescrições, exames e vínculos administrativos.' : 'Salve a consulta para liberar as ações gerenciais.' }}</p>
        </div>
      </div>

      <div v-if="appointment.id" class="appointment-manager-grid">
        <section class="appointment-manager-item">
          <div>
            <span>Convênio</span>
            <strong>{{ selectedInsuranceName }}</strong>
            <small>Defina a cobertura usada no atendimento.</small>
          </div>
          <v-select
            v-model="appointment.insuranceId"
            :items="insurances"
            item-text="name"
            item-value="id"
            outlined
            dense
            clearable hide-details="auto"
            no-data-text="Nenhum convênio encontrado"
            @change="saveAppointment(false)"
          ></v-select>
        </section>

        <section class="appointment-manager-item">
          <div>
            <span>Prescrições</span>
            <strong>Receitas e orientações</strong>
            <small>Abra uma prescrição vinculada ao paciente.</small>
          </div>
          <v-btn color="primary" outlined elevation="0" @click="openPrescriptionDialog">
            Criar prescrição
          </v-btn>
        </section>

        <section class="appointment-manager-item">
          <div>
            <span>Anamnese</span>
            <strong>{{ anamnesisSummary }}</strong>
            <small>Registre respostas clínicas durante a consulta.</small>
          </div>
          <v-btn color="primary" outlined elevation="0" @click="openAnamnesisDialog">
            {{ answeredAnamnesisCount ? "Continuar anamnese" : "Iniciar anamnese" }}
          </v-btn>
        </section>

        <section class="appointment-manager-item">
          <div>
            <span>Exames</span>
            <strong>Solicitações clínicas</strong>
            <small>Registre exames ou encaminhamentos laboratoriais.</small>
          </div>
          <v-btn color="primary" outlined elevation="0" @click="openTest">
            Solicitar exame
          </v-btn>
        </section>

        <section class="appointment-manager-item">
          <div>
            <span>Procedimentos</span>
            <strong>{{ selectedProcedureName }}</strong>
            <small>Atualize o procedimento principal da consulta.</small>
          </div>
          <v-select
            v-model="appointment.procedureId"
            :items="procedures"
            item-text="name"
            item-value="id"
            outlined
            dense hide-details="auto"
            no-data-text="Nenhum procedimento encontrado"
            @change="saveAppointment(false)"
          ></v-select>
        </section>
      </div>

      <div v-else class="appointment-manager-empty">
        <v-icon color="primary" size="32">mdi-clipboard-check-outline</v-icon>
        <strong>Consulta ainda não criada</strong>
        <span>Depois de salvar, este espaço abre o gerenciamento do atendimento.</span>
      </div>
    </v-card>

    <v-dialog v-model="patientCreateDialog" max-width="520" persistent>
      <v-card class="appointment-patient-dialog">
        <div class="appointment-patient-dialog__header">
          <div>
            <span>Novo paciente</span>
            <h3>Cadastro rápido</h3>
            <p>Informe os dados mínimos para seguir com a consulta.</p>
          </div>
          <v-btn icon small :disabled="patientCreateLoading" @click="closePatientCreateDialog">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="appointment-patient-dialog__body">
          <v-text-field
            id="quickPatientName"
            label="Nome completo"
            v-model="patientDraft.name"
            outlined
            dense hide-details="auto"
          ></v-text-field>
          <v-text-field
            id="quickPatientCpf"
            label="CPF"
            v-model="patientDraft.registration"
            v-mask="'###.###.###-##'"
            outlined
            dense hide-details="auto"
          ></v-text-field>
          <div class="appointment-patient-dialog__hint">
            O paciente será criado como cadastro incompleto. Depois, os demais dados podem ser revisados no módulo de pacientes.
          </div>
        </v-card-text>

        <v-card-actions class="appointment-patient-dialog__actions">
          <v-btn color="primary" outlined elevation="0" :disabled="patientCreateLoading" @click="closePatientCreateDialog">
            Cancelar
          </v-btn>
          <v-btn color="primary" elevation="0" :loading="patientCreateLoading" @click="createPatientFromAppointment">
            Criar paciente
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="anamnesisDialog" max-width="880" persistent scrollable>
      <v-card class="appointment-anamnesis-dialog">
        <div class="appointment-patient-dialog__header">
          <div>
            <span>Anamnese</span>
            <h3>Questionário clínico</h3>
            <p>Preencha as respostas relevantes para {{ selectedPatientName }} durante a consulta.</p>
          </div>
          <v-btn icon small :disabled="anamnesisLoading" @click="closeAnamnesisDialog">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="appointment-anamnesis-dialog__body">
          <div class="appointment-anamnesis-status">
            <div>
              <span>Progresso da anamnese</span>
              <strong>{{ answeredAnamnesisCount }} de {{ anamnesisDraft.length }} respostas</strong>
            </div>
            <v-progress-linear
              :value="anamnesisProgress"
              color="primary"
              height="8"
              rounded
            ></v-progress-linear>
          </div>

          <div class="appointment-anamnesis-list">
            <section
              v-for="(item, index) in anamnesisDraft"
              :key="index"
              class="appointment-anamnesis-question"
            >
              <div class="appointment-anamnesis-question__header">
                <span>{{ String(index + 1).padStart(2, "0") }}</span>
                <strong>{{ item.question }}</strong>
              </div>
              <v-textarea
                v-model="item.answer"
                label="Resposta"
                outlined
                dense
                rows="2"
                auto-grow
                hide-details="auto"
                placeholder="Descreva a resposta do paciente..."
              ></v-textarea>
            </section>
          </div>
        </v-card-text>

        <v-card-actions class="appointment-patient-dialog__actions">
          <v-btn color="primary" text elevation="0" :disabled="anamnesisLoading" @click="clearAnamnesisDraft">
            Limpar respostas
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined elevation="0" :disabled="anamnesisLoading" @click="closeAnamnesisDialog">
            Cancelar
          </v-btn>
          <v-btn color="primary" elevation="0" :loading="anamnesisLoading" @click="saveAnamnesis">
            Salvar anamnese
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="prescriptionDialog" max-width="920" persistent scrollable>
      <v-card class="appointment-prescription-dialog">
        <div class="appointment-patient-dialog__header">
          <div>
            <span>Prescrição</span>
            <h3>Receita e orientações</h3>
            <p>Crie uma prescrição para {{ selectedPatientName }} sem sair da consulta.</p>
          </div>
          <v-btn icon small :disabled="prescriptionLoading" @click="closePrescriptionDialog">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="appointment-prescription-dialog__body">
          <div class="appointment-prescription-patient">
            <span>Paciente vinculado</span>
            <strong>{{ selectedPatientName }}</strong>
            <small>Consulta #{{ appointment.id }}</small>
          </div>

          <div class="appointment-prescription-section">
            <div class="appointment-prescription-section__header">
              <div>
                <h4>Medicamentos prescritos</h4>
                <p>Informe medicamento, dosagem, quantidade e posologia.</p>
              </div>
              <v-btn color="primary" elevation="0" small @click="addPrescriptionItem">
                <v-icon left small>mdi-plus</v-icon>
                Adicionar
              </v-btn>
            </div>

            <div v-if="prescriptionDraft.items.length === 0" class="appointment-prescription-empty">
              <v-icon size="34" color="primary">mdi-pill-off</v-icon>
              <strong>Nenhum medicamento adicionado</strong>
              <span>Adicione ao menos um medicamento para salvar a prescrição.</span>
            </div>

            <div
              v-for="(item, index) in prescriptionDraft.items"
              :key="index"
              class="appointment-prescription-item"
            >
              <div class="appointment-prescription-item__header">
                <strong>Medicamento {{ index + 1 }}</strong>
                <v-btn icon x-small color="error" @click="removePrescriptionItem(index)">
                  <v-icon size="18">mdi-close</v-icon>
                </v-btn>
              </div>
              <div class="appointment-prescription-item__grid">
                <v-text-field
                  v-model="item.medicationName"
                  label="Nome do medicamento"
                  outlined
                  dense
                  hide-details="auto"
                  placeholder="Ex: Amoxicilina 500mg"
                ></v-text-field>
                <v-text-field
                  v-model="item.dosage"
                  label="Dosagem"
                  outlined
                  dense
                  hide-details="auto"
                  placeholder="Ex: 500mg"
                ></v-text-field>
                <v-text-field
                  v-model="item.quantity"
                  label="Quantidade"
                  outlined
                  dense
                  hide-details="auto"
                  placeholder="Ex: 1 caixa"
                ></v-text-field>
                <v-text-field
                  v-model="item.instructions"
                  label="Via / forma"
                  outlined
                  dense
                  hide-details="auto"
                  placeholder="Ex: Oral"
                ></v-text-field>
                <v-text-field
                  v-model="item.posology"
                  class="appointment-prescription-item__wide"
                  label="Posologia"
                  outlined
                  dense
                  hide-details="auto"
                  placeholder="Ex: Tomar 1 comprimido a cada 8 horas por 7 dias"
                ></v-text-field>
              </div>
            </div>
          </div>

          <v-textarea
            v-model="prescriptionDraft.notes"
            label="Observações"
            outlined
            dense
            rows="3"
            auto-grow
            hide-details="auto"
            placeholder="Orientações clínicas, restrições e recomendações ao paciente..."
          ></v-textarea>
        </v-card-text>

        <v-card-actions class="appointment-patient-dialog__actions">
          <v-btn color="primary" outlined elevation="0" :disabled="prescriptionLoading" @click="closePrescriptionDialog">
            Cancelar
          </v-btn>
          <v-btn color="primary" elevation="0" :loading="prescriptionLoading" @click="savePrescriptionFromAppointment">
            Criar prescrição
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="loadingPage">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import axios from "axios";
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Blockquote,
  HardBreak,
  History,
} from "tiptap-vuetify";

export default {
  name: "AppointmentsDetails",

  components: {
    TiptapVuetify,
  },

  data: () => ({
    appointment: {
      id: null,
      scheduledDate: "",
      scheduledTime: "",
      description: "",
      patientId: null,
      procedureId: null,
      insuranceId: null,
      state: 2,
      anamnesis: [],
    },
    patients: [],
    procedures: [],
    insurances: [],
    patientSearch: "",
    patientCreateDialog: false,
    patientCreateLoading: false,
    patientDraft: {
      name: "",
      registration: "",
    },
    prescriptionDialog: false,
    prescriptionLoading: false,
    prescriptionDraft: {
      notes: "",
      items: [],
    },
    anamnesisDialog: false,
    anamnesisLoading: false,
    anamnesisDraft: [],
    anamnesisQuestions: [
      "Qual é a queixa principal do paciente?",
      "Possui alergias conhecidas? Quais?",
      "Faz uso de medicação contínua?",
      "Possui alguma doença sistêmica?",
      "Já realizou cirurgia ou internação relevante?",
      "Está em tratamento médico atualmente?",
      "Tem histórico de sangramento ou cicatrização lenta?",
      "Observações clínicas adicionais",
    ],
    states: [
      { text: "Agendado", value: 2 },
      { text: "Confirmado", value: 12 },
      { text: "Cancelado", value: 11 },
      { text: "Finalizado", value: 10 },
    ],
    editorExtensions: [
      History,
      Blockquote,
      Underline,
      Strike,
      Italic,
      ListItem,
      BulletList,
      OrderedList,
      [Heading, { options: { levels: [2, 3, 4] } }],
      Bold,
      Paragraph,
      HardBreak,
    ],

    loadingPage: false,
    loadingSave: false,
    formValid: false,

    rules: {
      required: (value) => !!value || "Campo obrigatório.",
    },
  }),

  computed: {
    pageTitle() {
      return this.appointment.id ? "Detalhes da Consulta" : "Nova Consulta";
    },
    appointmentDateLabel() {
      if (!this.appointment.scheduledDate && !this.appointment.scheduledTime) return "-";
      const date = this.appointment.scheduledDate ? this.formatDate(this.appointment.scheduledDate) : "Sem data";
      return this.appointment.scheduledTime ? `${date} às ${this.appointment.scheduledTime}` : date;
    },
    selectedPatientName() {
      return this.findById(this.patients, this.appointment.patientId)?.name || "-";
    },
    normalizedPatientSearch() {
      return (this.patientSearch || "").trim();
    },
    canCreatePatientFromSearch() {
      const search = this.normalizedPatientSearch.toLowerCase();
      if (search.length < 3) return false;
      return !this.patients.some((patient) => String(patient.name || "").trim().toLowerCase() === search);
    },
    selectedProcedureName() {
      return this.findById(this.procedures, this.appointment.procedureId)?.name || "-";
    },
    selectedInsuranceName() {
      return this.findById(this.insurances, this.appointment.insuranceId)?.name || "Sem convênio";
    },
    selectedStateText() {
      return this.findById(this.states, this.appointment.state, "value")?.text || "-";
    },
    answeredAnamnesisCount() {
      return this.normalizedAnamnesis(this.appointment.anamnesis)
        .filter((item) => String(item.answer || "").trim()).length;
    },
    anamnesisSummary() {
      return this.answeredAnamnesisCount
        ? `${this.answeredAnamnesisCount} resposta(s)`
        : "Não iniciada";
    },
    anamnesisProgress() {
      if (!this.anamnesisDraft.length) return 0;
      const answered = this.anamnesisDraft.filter((item) => String(item.answer || "").trim()).length;
      return Math.round((answered / this.anamnesisDraft.length) * 100);
    },
  },

  watch: {
    "$route.params.id": {
      async handler(id) {
        if (id) {
          this.loadingPage = true;
          await this.loadAppointment(id);
          this.loadingPage = false;
          return;
        }

        this.resetAppointment();
      },
    },
  },

  async created() {
    this.loadingPage = true;
    const appointmentId = this.$route.params.id;

    await Promise.all([this.loadPatients(), this.loadProcedures(), this.loadInsurances()]);

    if (appointmentId) {
      await this.loadAppointment(appointmentId);
    }
    this.loadingPage = false;
  },

  methods: {
    findById(items, id, key = "id") {
      return items.find((item) => Number(item[key]) === Number(id));
    },

    formatDate(value) {
      if (!value) return "—";
      const isoDate = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoDate) return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("pt-BR");
    },

    resetAppointment() {
      this.appointment = {
        id: null,
        scheduledDate: "",
        scheduledTime: "",
        description: "",
        patientId: null,
        procedureId: null,
        insuranceId: null,
        state: 2,
        anamnesis: [],
      };
    },

    normalizedAnamnesis(value) {
      if (Array.isArray(value)) {
        return value.map((item) => ({
          question: String(item?.question || "").trim(),
          answer: String(item?.answer || "").trim(),
        })).filter((item) => item.question || item.answer);
      }

      if (typeof value === "string" && value.trim()) {
        try {
          return this.normalizedAnamnesis(JSON.parse(value));
        } catch (_) {
          return [{ question: "Anamnese", answer: value.trim() }];
        }
      }

      return [];
    },

    buildAnamnesisDraft() {
      const current = this.normalizedAnamnesis(this.appointment.anamnesis);
      const byQuestion = current.reduce((acc, item) => {
        acc[String(item.question || "").trim().toLowerCase()] = item.answer || "";
        return acc;
      }, {});

      const base = this.anamnesisQuestions.map((question) => ({
        question,
        answer: byQuestion[question.trim().toLowerCase()] || "",
      }));

      current.forEach((item) => {
        const exists = base.some((question) => question.question.trim().toLowerCase() === item.question.trim().toLowerCase());
        if (!exists && item.question) base.push({ ...item });
      });

      return base;
    },

    openAnamnesisDialog() {
      this.anamnesisDraft = this.buildAnamnesisDraft();
      this.anamnesisDialog = true;
    },

    closeAnamnesisDialog() {
      if (this.anamnesisLoading) return;
      this.anamnesisDialog = false;
    },

    clearAnamnesisDraft() {
      this.anamnesisDraft = this.anamnesisDraft.map((item) => ({
        ...item,
        answer: "",
      }));
    },

    async saveAnamnesis() {
      this.anamnesisLoading = true;
      try {
        this.appointment.anamnesis = this.anamnesisDraft
          .map((item) => ({
            question: String(item.question || "").trim(),
            answer: String(item.answer || "").trim(),
          }))
          .filter((item) => item.question && item.answer);

        const saved = await this.saveAppointment(false);
        if (!saved) return;

        this.$dialog.notify.success("Anamnese salva na consulta.", { position: "top-right", timeout: 3000 });
        this.anamnesisDialog = false;
      } catch (error) {
        console.error("Erro ao salvar anamnese:", error);
        const message = this.$codes ? this.$codes(error) : "Erro ao salvar anamnese.";
        this.$dialog.notify.error(message, { position: "top-right", timeout: 5000 });
      } finally {
        this.anamnesisLoading = false;
      }
    },

    normalizeList(response) {
      if (Array.isArray(response.data?.data)) return response.data.data;
      if (Array.isArray(response.data)) return response.data;
      return [];
    },

    async loadPatients() {
      try {
        const response = await axios.get("/patients", { params: { start: 0, limit: 100 } });
        this.patients = this.normalizeList(response);
      } catch (error) {
        console.error("Erro ao carregar pacientes:", error);
        this.$dialog.notify.error("Erro ao carregar lista de pacientes.", { position: "top-right", timeout: 5000 });
      }
    },

    openPatientCreateDialog() {
      this.patientDraft = {
        name: this.normalizedPatientSearch,
        registration: "",
      };
      this.patientCreateDialog = true;
    },

    closePatientCreateDialog() {
      if (this.patientCreateLoading) return;
      this.patientCreateDialog = false;
    },

    normalizeCpf(value) {
      return String(value || "").replace(/\D/g, "");
    },

    async createPatientFromAppointment() {
      const name = (this.patientDraft.name || "").trim();
      const registration = this.normalizeCpf(this.patientDraft.registration);

      if (!name) {
        this.$dialog.notify.warn("Informe o nome completo do paciente.", { position: "top-right", timeout: 3000 });
        return;
      }

      if (registration.length !== 11) {
        this.$dialog.notify.warn("Informe um CPF válido para criar o paciente.", { position: "top-right", timeout: 3000 });
        return;
      }

      this.patientCreateLoading = true;
      try {
        const response = await axios.post("/patients", {
          name,
          registration,
        });
        const created = response.data?.data || response.data || {};
        const patient = {
          id: created.id,
          name: created.name || name,
          registration: created.registration || registration,
        };

        if (!patient.id) {
          await this.loadPatients();
          const loadedPatient = this.patients.find((item) => this.normalizeCpf(item.registration) === registration);
          patient.id = loadedPatient?.id;
        }

        if (!patient.id) {
          throw new Error("Paciente criado sem identificador retornado pela API.");
        }

        this.patients = [
          patient,
          ...this.patients.filter((item) => Number(item.id) !== Number(patient.id)),
        ];
        this.appointment.patientId = patient.id;
        this.patientSearch = patient.name;
        this.patientCreateDialog = false;
        this.$dialog.notify.success("Paciente criado e selecionado na consulta.", { position: "top-right", timeout: 3000 });
      } catch (error) {
        console.error("Erro ao criar paciente rápido:", error);
        const message = this.$codes ? this.$codes(error) : "Erro ao criar paciente.";
        this.$dialog.notify.error(message, { position: "top-right", timeout: 5000 });
      } finally {
        this.patientCreateLoading = false;
      }
    },

    async loadProcedures() {
      try {
        const response = await axios.get("/procedures", { params: { start: 0, limit: 100 } });
        this.procedures = this.normalizeList(response);
      } catch (error) {
        console.error("Erro ao carregar procedimentos:", error);
        this.$dialog.notify.error("Erro ao carregar lista de procedimentos.", { position: "top-right", timeout: 5000 });
      }
    },

    async loadInsurances() {
      try {
        const response = await axios.get("/insurances", { params: { start: 0, limit: 100 } });
        this.insurances = this.normalizeList(response);
      } catch (error) {
        console.error("Erro ao carregar convênios:", error);
        this.$dialog.notify.error("Erro ao carregar lista de convênios.", { position: "top-right", timeout: 5000 });
      }
    },

    async loadAppointment(id) {
      try {
        const response = await axios.get(`/appointments/${id}`);
        const data = response.data.data;
        this.appointment = {
          id: data.id,
          scheduledDate: data.scheduledDate || "",
          scheduledTime: data.scheduledTime || "",
          description: data.description || "",
          patientId: data.patient ? Number(data.patient.id) : null,
          procedureId: data.procedure ? Number(data.procedure.id) : null,
          insuranceId: data.insurance ? Number(data.insurance.id) : null,
          state: data.state || 2,
          anamnesis: this.normalizedAnamnesis(data.anamnesis),
        };
      } catch (error) {
        console.error("Erro ao carregar detalhes da consulta:", error);
        this.$dialog.notify.error("Erro ao carregar detalhes da consulta.", { position: "top-right", timeout: 5000 });
      }
    },

    async saveAppointment(showSuccess = true) {
      if (!this.$refs.appointmentForm.validate()) return false;

      this.loadingSave = true;
      try {
        const payload = {
          scheduledDate: this.appointment.scheduledDate,
          scheduledTime: this.appointment.scheduledTime,
          description: this.appointment.description,
          patient: this.appointment.patientId,
          procedure: this.appointment.procedureId,
          insurance: this.appointment.insuranceId,
          state: this.appointment.state,
          anamnesis: this.normalizedAnamnesis(this.appointment.anamnesis),
        };

        if (this.appointment.id) {
          await axios.patch(`/appointments/${this.appointment.id}`, { ...payload, operation: "update" });
          if (showSuccess) {
            this.$dialog.notify.success("Consulta atualizada com sucesso!", { position: "top-right", timeout: 3000 });
          }
        } else {
          const response = await axios.post("/appointments", payload);
          const createdId = response.data.data.id;
          this.appointment.id = createdId;
          this.$dialog.notify.success("Consulta criada com sucesso!", { position: "top-right", timeout: 3000 });
          await this.$router.replace(`/consultas/${createdId}`);
          await this.loadAppointment(createdId);
        }
        return true;
      } catch (error) {
        console.error("Erro ao salvar consulta:", error);
        this.$dialog.notify.error("Erro ao salvar consulta. Verifique os dados.", { position: "top-right", timeout: 5000 });
        return false;
      } finally {
        this.loadingSave = false;
      }
    },

    createEmptyPrescriptionItem() {
      return {
        medicationName: "",
        dosage: "",
        quantity: "",
        instructions: "",
        posology: "",
      };
    },

    resetPrescriptionDraft() {
      this.prescriptionDraft = {
        notes: "",
        items: [this.createEmptyPrescriptionItem()],
      };
    },

    openPrescriptionDialog() {
      if (!this.appointment.patientId) {
        this.$dialog.notify.warn("Selecione um paciente na consulta antes de criar a prescrição.", { position: "top-right", timeout: 3000 });
        return;
      }

      this.resetPrescriptionDraft();
      this.prescriptionDialog = true;
    },

    closePrescriptionDialog() {
      if (this.prescriptionLoading) return;
      this.prescriptionDialog = false;
    },

    addPrescriptionItem() {
      this.prescriptionDraft.items.push(this.createEmptyPrescriptionItem());
    },

    removePrescriptionItem(index) {
      this.prescriptionDraft.items.splice(index, 1);
    },

    isPrescriptionItemFilled(item) {
      return [
        item.medicationName,
        item.dosage,
        item.quantity,
        item.instructions,
        item.posology,
      ].some((value) => String(value || "").trim());
    },

    async savePrescriptionFromAppointment() {
      const items = this.prescriptionDraft.items
        .filter((item) => this.isPrescriptionItemFilled(item))
        .map((item) => ({
          medicationName: String(item.medicationName || "").trim(),
          dosage: String(item.dosage || "").trim(),
          quantity: String(item.quantity || "").trim(),
          instructions: String(item.instructions || "").trim(),
          posology: String(item.posology || "").trim(),
        }));

      if (items.length === 0) {
        this.$dialog.notify.warn("Adicione ao menos um medicamento para criar a prescrição.", { position: "top-right", timeout: 3000 });
        return;
      }

      this.prescriptionLoading = true;
      try {
        await axios.post("/prescriptions", {
          patientId: this.appointment.patientId,
          notes: this.prescriptionDraft.notes || undefined,
          items,
        });
        this.$dialog.notify.success("Prescrição criada para o paciente da consulta.", { position: "top-right", timeout: 3000 });
        this.prescriptionDialog = false;
      } catch (error) {
        console.error("Erro ao criar prescrição pela consulta:", error);
        const message = this.$codes ? this.$codes(error) : "Erro ao criar prescrição.";
        this.$dialog.notify.error(message, { position: "top-right", timeout: 5000 });
      } finally {
        this.prescriptionLoading = false;
      }
    },

    openTest() {
      this.$router.push({
        path: "/exames/novo",
        query: {
          appointment: this.appointment.id,
          patient: this.appointment.patientId,
        },
      });
    },
  },
};
</script>

<style scoped>
.appointment-page {
  gap: 22px;
}

.appointment-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 22px;
}

.appointment-summary__card,
.appointment-manager-item {
  background: #f6f7f5;
  border: 1px solid #edf0ec;
  border-radius: 8px;
}

.appointment-summary__card {
  min-height: 116px;
  padding: 24px 26px;
}

.appointment-summary__card span,
.appointment-manager-item span {
  display: block;
  color: #7b8580;
  font-size: 14px;
  font-weight: 500;
}

.appointment-summary__card strong {
  display: block;
  color: #17201c;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.2;
  margin-top: 10px;
}

.appointment-summary__card small,
.appointment-manager-item small {
  display: block;
  color: #a64bd2;
  font-size: 13px;
  font-weight: 700;
  margin-top: 10px;
}

.appointment-form-card,
.appointment-manager-card {
  padding: 0;
  overflow: hidden;
}

.appointment-card-header {
  align-items: flex-start;
  border-bottom: 1px solid #eef0ed;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 28px 32px 20px;
}

.appointment-card-header h2 {
  color: #17201c;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.15;
  margin: 0;
}

.appointment-card-header p {
  color: #7b8580;
  font-size: 15px;
  line-height: 1.35;
  margin: 8px 0 0;
}

.appointment-id-chip {
  background: #f1e8fb !important;
  color: #8f4bd0 !important;
  font-weight: 800;
}

.appointment-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 18px;
  padding: 24px 32px 8px;
}

.appointment-create-patient {
  padding: 0 8px 8px;
}

.appointment-create-patient__button {
  align-items: center;
  background: #f8f1fd;
  border: 1px solid #e6c9f7;
  border-radius: 8px;
  color: #8f52d1;
  display: flex;
  font-size: 14px;
  font-weight: 800;
  gap: 10px;
  min-height: 42px;
  padding: 0 12px;
  text-align: left;
  width: 100%;
}

.appointment-create-patient__button:hover {
  background: #f1e4fb;
}

.appointment-editor-block {
  padding: 6px 32px 24px;
}

.appointment-editor-block__title {
  margin-bottom: 12px;
}

.appointment-editor-block__title span {
  color: #17201c;
  display: block;
  font-size: 15px;
  font-weight: 800;
}

.appointment-editor-block__title small {
  color: #7b8580;
  display: block;
  font-size: 13px;
  margin-top: 4px;
}

.appointment-rich-editor {
  border: 1px solid #e2e7e4;
  border-radius: 8px;
  overflow: hidden;
}

::v-deep .appointment-rich-editor .tiptap-vuetify-editor__toolbar {
  background: #f8f9f7 !important;
  border-bottom: 1px solid #e8ece8;
}

::v-deep .appointment-rich-editor .ProseMirror {
  min-height: 150px;
  padding: 18px;
}

::v-deep .appointment-rich-editor .ProseMirror:focus {
  outline: none;
}

.appointment-actions {
  border-top: 1px solid #eef0ed;
  margin: 0;
  padding: 20px 32px 28px;
}

.appointment-manager-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  padding: 28px 32px 32px;
}

.appointment-manager-item {
  align-items: center;
  display: grid;
  grid-template-columns: 1fr minmax(210px, 260px);
  gap: 18px;
  min-height: 132px;
  padding: 22px;
}

.appointment-manager-item strong {
  color: #17201c;
  display: block;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
  margin-top: 8px;
}

.appointment-manager-empty {
  align-items: center;
  background: #f6f7f5;
  border: 1px dashed #dce3df;
  border-radius: 8px;
  color: #7b8580;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  margin: 28px 32px 32px;
  min-height: 180px;
  text-align: center;
}

.appointment-manager-empty strong {
  color: #17201c;
  font-size: 16px;
  font-weight: 800;
}

.appointment-patient-dialog {
  border-radius: 8px !important;
  overflow: hidden;
}

.appointment-anamnesis-dialog {
  border-radius: 8px !important;
  overflow: hidden;
}

.appointment-prescription-dialog {
  border-radius: 8px !important;
  overflow: hidden;
}

.appointment-patient-dialog__header {
  align-items: flex-start;
  border-bottom: 1px solid #eef0ed;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 28px 18px;
}

.appointment-patient-dialog__header span {
  color: #a64bd2;
  display: block;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.appointment-patient-dialog__header h3 {
  color: #17201c;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.15;
  margin: 0;
}

.appointment-patient-dialog__header p {
  color: #7b8580;
  font-size: 14px;
  line-height: 1.35;
  margin: 8px 0 0;
}

.appointment-patient-dialog__body {
  display: grid;
  gap: 14px;
  padding: 24px 28px 8px !important;
}

.appointment-patient-dialog__hint {
  background: #f6f7f5;
  border: 1px solid #edf0ec;
  border-radius: 8px;
  color: #66706b;
  font-size: 13px;
  line-height: 1.45;
  padding: 12px 14px;
}

.appointment-patient-dialog__actions {
  border-top: 1px solid #eef0ed;
  gap: 10px;
  justify-content: flex-end;
  padding: 18px 28px 24px !important;
}

.appointment-anamnesis-dialog__body {
  display: grid;
  gap: 18px;
  padding: 22px 28px !important;
}

.appointment-anamnesis-status {
  background: #f6f7f5;
  border: 1px solid #edf0ec;
  border-radius: 8px;
  display: grid;
  gap: 14px;
  padding: 16px 18px;
}

.appointment-anamnesis-status span {
  color: #7b8580;
  display: block;
  font-size: 13px;
  font-weight: 600;
}

.appointment-anamnesis-status strong {
  color: #17201c;
  display: block;
  font-size: 18px;
  font-weight: 800;
  margin-top: 4px;
}

.appointment-anamnesis-list {
  display: grid;
  gap: 12px;
}

.appointment-anamnesis-question {
  border: 1px solid #edf0ec;
  border-radius: 8px;
  display: grid;
  gap: 12px;
  padding: 16px;
}

.appointment-anamnesis-question__header {
  align-items: center;
  display: flex;
  gap: 12px;
}

.appointment-anamnesis-question__header span {
  align-items: center;
  background: #f1e8fb;
  border-radius: 999px;
  color: #8f4bd0;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 800;
  height: 28px;
  justify-content: center;
  width: 28px;
}

.appointment-anamnesis-question__header strong {
  color: #17201c;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.25;
}

.appointment-prescription-dialog__body {
  display: grid;
  gap: 18px;
  padding: 22px 28px !important;
}

.appointment-prescription-patient {
  background: #f6f7f5;
  border: 1px solid #edf0ec;
  border-radius: 8px;
  padding: 16px 18px;
}

.appointment-prescription-patient span,
.appointment-prescription-patient small {
  color: #7b8580;
  display: block;
  font-size: 13px;
  font-weight: 600;
}

.appointment-prescription-patient strong {
  color: #17201c;
  display: block;
  font-size: 20px;
  font-weight: 800;
  margin: 4px 0;
}

.appointment-prescription-section {
  border: 1px solid #edf0ec;
  border-radius: 8px;
  overflow: hidden;
}

.appointment-prescription-section__header,
.appointment-prescription-item__header {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
}

.appointment-prescription-section__header {
  background: #fbfcfb;
  border-bottom: 1px solid #edf0ec;
  padding: 16px 18px;
}

.appointment-prescription-section__header h4 {
  color: #17201c;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
}

.appointment-prescription-section__header p {
  color: #7b8580;
  font-size: 13px;
  line-height: 1.35;
  margin: 4px 0 0;
}

.appointment-prescription-empty {
  align-items: center;
  color: #7b8580;
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  min-height: 140px;
  padding: 24px;
  text-align: center;
}

.appointment-prescription-empty strong {
  color: #17201c;
  font-size: 15px;
  font-weight: 800;
}

.appointment-prescription-item {
  padding: 18px;
}

.appointment-prescription-item + .appointment-prescription-item {
  border-top: 1px solid #edf0ec;
}

.appointment-prescription-item__header {
  margin-bottom: 14px;
}

.appointment-prescription-item__header strong {
  color: #17201c;
  font-size: 15px;
  font-weight: 800;
}

.appointment-prescription-item__grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.appointment-prescription-item__wide {
  grid-column: 1 / -1;
}

@media (max-width: 1260px) {
  .appointment-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .appointment-manager-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 780px) {
  .appointment-summary,
  .appointment-form-grid {
    grid-template-columns: 1fr;
  }

  .appointment-card-header,
  .appointment-form-grid,
  .appointment-editor-block,
  .appointment-actions,
  .appointment-manager-grid {
    padding-left: 20px;
    padding-right: 20px;
  }

  .appointment-manager-item {
    grid-template-columns: 1fr;
  }

  .appointment-prescription-item__grid {
    grid-template-columns: 1fr;
  }
}
</style>
